import React, { useEffect, useRef } from "react";
import type { WBSActivity } from "../../../models";
import "./ActivityTable.css";

interface Props {
  activities: WBSActivity[];
}

function statusLabel(status: string): string {
  switch (status) {
    case "TK_Complete":
      return "Done";
    case "TK_Active":
      return "Active";
    default:
      return "NS";
  }
}

function statusClass(status: string): string {
  switch (status) {
    case "TK_Complete":
      return "act-complete";
    case "TK_Active":
      return "act-active";
    default:
      return "act-notstart";
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  const mon = d.toLocaleString("en-US", { month: "short" });
  return `${mon} ${d.getDate()}`;
}

function fmtNum(val: number | null): string {
  if (val == null) return "\u2014";
  return Number.isInteger(val) ? String(val) : val.toFixed(1);
}

function floatClass(val: number | null): string {
  if (val == null) return "";
  if (val <= 0) return "act-float-critical";
  if (val <= 5) return "act-float-near";
  return "";
}

const ActivityTable: React.FC<Props> = ({ activities }) => {
  const tableRef = useRef<HTMLDivElement>(null);

  // Prevent trackpad horizontal swipe from triggering browser back/forward.
  // Horizontal delta scrolls the table natively; vertical passes through to parent.
  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      // Only intercept when there's meaningful horizontal intent
      if (Math.abs(e.deltaX) > 0) {
        // Check if the table can scroll in the swiped direction
        const atLeft = el.scrollLeft <= 0;
        const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
        const goingLeft = e.deltaX < 0;
        const goingRight = e.deltaX > 0;

        // If we're NOT at the edge in the scroll direction, consume the event
        // If we ARE at the edge, still prevent to avoid back/forward nav
        if (el.scrollWidth > el.clientWidth) {
          e.preventDefault();
          if (!(atLeft && goingLeft) && !(atRight && goingRight)) {
            el.scrollLeft += e.deltaX;
          }
        } else if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          // Table doesn't overflow but swipe is mostly horizontal — block nav
          e.preventDefault();
        }
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  if (activities.length === 0) return null;

  return (
    <div className="act-table" ref={tableRef}>
      {/* Header */}
      <div className="act-row act-header">
        <span className="act-col act-col-takt">Takt Code</span>
        <span className="act-col act-col-id">P6 ID</span>
        <span className="act-col act-col-name">Name</span>
        <span className="act-col act-col-status">Status</span>
        <span className="act-col act-col-num">Dur</span>
        <span className="act-col act-col-num">Rem</span>
        <span className="act-col act-col-num">TF</span>
        <span className="act-col act-col-num">FF</span>
        <span className="act-col act-col-date">Tgt S</span>
        <span className="act-col act-col-date">Tgt F</span>
        <span className="act-col act-col-date">Early S</span>
        <span className="act-col act-col-date">Early F</span>
        <span className="act-col act-col-date">Late S</span>
        <span className="act-col act-col-date">Late F</span>
        <span className="act-col act-col-date">Act S</span>
        <span className="act-col act-col-date">Act F</span>
      </div>

      {/* Data rows */}
      {activities.map((act) => (
        <div key={act.id} className={`act-row ${statusClass(act.status)}`}>
          <span className="act-col act-col-takt act-takt-val">
            {act.takt_code || "\u2014"}
          </span>
          <span className="act-col act-col-id act-id-val">{act.task_code}</span>
          <span className="act-col act-col-name act-name-val" title={act.task_name}>
            {act.task_name}
          </span>
          <span className={`act-col act-col-status act-status-val ${statusClass(act.status)}`}>
            {statusLabel(act.status)}
          </span>
          <span className="act-col act-col-num">{fmtNum(act.original_duration)}</span>
          <span className="act-col act-col-num">{fmtNum(act.remaining_duration)}</span>
          <span className={`act-col act-col-num ${floatClass(act.total_float)}`}>
            {fmtNum(act.total_float)}
          </span>
          <span className={`act-col act-col-num ${floatClass(act.free_float)}`}>
            {fmtNum(act.free_float)}
          </span>
          <span className="act-col act-col-date">{fmtDate(act.target_start)}</span>
          <span className="act-col act-col-date">{fmtDate(act.target_finish)}</span>
          <span className="act-col act-col-date">{fmtDate(act.early_start)}</span>
          <span className="act-col act-col-date">{fmtDate(act.early_finish)}</span>
          <span className="act-col act-col-date">{fmtDate(act.late_start)}</span>
          <span className="act-col act-col-date">{fmtDate(act.late_finish)}</span>
          <span className="act-col act-col-date">{fmtDate(act.actual_start)}</span>
          <span className="act-col act-col-date">{fmtDate(act.actual_finish)}</span>
        </div>
      ))}
    </div>
  );
};

export default ActivityTable;
